import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { Maybe } from '__generated__/graphql-types';
import type {
  EnterpriseBadge_BadgeCollectionWithUserState,
  EnterpriseBadge_ProductId,
  Skill_Skill,
} from '__generated__/graphql-types-do-not-use';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import User from 'js/lib/user';

import type { CollectionType } from 'bundles/enterprise-collections/components/CollectionItem';
import EnterpriseBadgeCollectionsQuery from 'bundles/enterprise-learner-onboarding/queries/EnterpriseBadgeCollectionsQuery.graphql';
import type {
  EnterpriseBadgeCollectionsQuery as EnterpriseBadgeCollectionsQueryData,
  EnterpriseBadgeCollectionsQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/EnterpriseBadgeCollectionsQuery';
import { EnterpriseBadgesCoursesSessionDatesQuery } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import type {
  EnterpriseBadgesCoursesSessionDatesQuery as EnterpriseBadgesCoursesSessionDatesQueryData,
  EnterpriseBadgesCoursesSessionDatesQueryVariables,
  EnterpriseBadgesCoursesSessionDatesQuery_EnterpriseProgramSessionAssociationsV1Resource_byProgramAndCourses_elements_session as Session,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/__generated__/EnterpriseBadgesCoursesSessionDatesQuery';
import ProductCoursesQuery from 'bundles/enterprise-legacy-learner-home/queries/ProductCoursesQuery.graphql';
import type { EnterpriseProgramFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import type {
  ProductCardCourseFragmentFragment as Course,
  ProductCoursesQuery as ProductCoursesQueryType,
  ProductCoursesQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductCoursesQuery';
import type { Item, Product, ProductList } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import EnterpriseBadgeCollectionsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseBadgeCollectionsContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

export type EnterpriseBadge = { __typename?: 'EnterpriseBadge_badgeCollectionWithUserState' } & Pick<
  EnterpriseBadge_BadgeCollectionWithUserState,
  | 'title'
  | 'collectionId'
  | 'badgeTemplateId'
  | 'description'
  | 'learningObjectives'
  | 'imageUrl'
  | 'userState'
  | 'slug'
> & {
    productIds: Array<{ __typename?: 'EnterpriseBadge_productId' } & Pick<EnterpriseBadge_ProductId, 'id' | 'type'>>;
    skillsMetadata: Array<Maybe<{ __typename?: 'Skill_Skill' } & Pick<Skill_Skill, 'id' | 'skillName'>>>;
  };

export type EnterpriseBadgeWithCollection = {
  __typename?: 'EnterpriseBadge_badgeCollectionWithUserState';
  id: string;
  enterpriseBadge: EnterpriseBadge;
  collection: CollectionType;
};

const enterpriseBadgeCollectionToCollectionType = (
  enterpriseBadge: EnterpriseBadge,
  courseMetadata: Record<string, Course>,
  sessions: Array<Session>
) => {
  const courses: ProductList = { elements: [] };
  const items: Array<Item> = [];

  enterpriseBadge.productIds.forEach((productId) => {
    if (courseMetadata[productId.id]) {
      items.push({
        productId: tupleToStringKey(['VerifiedCertificate', productId.id]),
      });
      const productFragment = courseMetadata[productId.id];
      courses.elements.push({
        ...productFragment,
        courseTypeMetadata: {
          courseTypeMetadata: {
            // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            __typename: productFragment.courseTypeMetadata.metadata.__typename || '',
          },
        },
        partners: { elements: productFragment?.partners },
      } as Product);
    }
  });

  return {
    id: enterpriseBadge.collectionId,
    collectionTrackingId: enterpriseBadge.collectionId,
    title: enterpriseBadge.title,
    description: {},
    courses,
    s12ns: {
      elements: [],
    },
    items,
    associatedSessions: {
      elements: sessions,
    },
  } as CollectionType;
};

type PropsForBase = {
  programId?: string;
  limit?: number;
  children: React.ReactNode;
};

type Props = {
  programFromProps?: EnterpriseProgramFromSlugFragment;
  limit?: number;
  children: React.ReactNode;
};

const DEFAULT_BADGE_COLLECTIONS_LIMIT = 1000;

const EnterpriseBadgeCollectionsProviderBase = ({
  programId,
  limit = DEFAULT_BADGE_COLLECTIONS_LIMIT,
  children,
}: PropsForBase) => {
  const userId = User.get().id;
  const {
    data: badgeCollectionsData,
    loading: badgeCollectionsLoading,
    refetch: refetchEnterpriseBadgeCollections,
  } = useQuery<EnterpriseBadgeCollectionsQueryData, EnterpriseBadgeCollectionsQueryVariables>(
    EnterpriseBadgeCollectionsQuery,
    {
      variables: {
        userId: String(userId),
        programId: programId || '',
        limit,
      },
      context: { clientName: 'gatewayGql' },
      skip: !programId || !userId,
      ssr: false,
      errorPolicy: 'all' as const,
    }
  );

  const enterpriseBadgeCollections = useMemo(
    () => badgeCollectionsData?.EnterpriseBadge?.findByUserAndProgram.badgeCollectionsWithUserState || [],
    [badgeCollectionsData]
  );

  const courseIds = useMemo(
    () => enterpriseBadgeCollections.flatMap((badge) => badge.productIds.map((product) => product.id)),
    [enterpriseBadgeCollections]
  );

  const { data: badgeCoursesData, loading: badgeCoursesLoading } = useQuery<
    ProductCoursesQueryType,
    ProductCoursesQueryVariables
  >(ProductCoursesQuery, {
    variables: {
      courseIds,
    },
    skip: !courseIds.length,
    ssr: false,
    errorPolicy: 'all' as const,
    context: {
      clientName: 'gatewayGql',
    },
  });

  const courseMetadata: Record<string, Course> = {};
  const courses = badgeCoursesData?.Course?.queryByIds || [];

  courses.forEach((course) => {
    if (!course) {
      return;
    }
    courseMetadata[course.id] = course;
  });

  const { data: badgeCoursesSessionData, loading: badgeCoursesSessionLoading } = useQuery<
    EnterpriseBadgesCoursesSessionDatesQueryData,
    EnterpriseBadgesCoursesSessionDatesQueryVariables
  >(EnterpriseBadgesCoursesSessionDatesQuery, {
    variables: {
      courseIds,
      programId: programId || '',
    },
    skip: !programId || !courseIds.length,
    ssr: false,
    errorPolicy: 'all' as const,
  });

  const sessions =
    badgeCoursesSessionData?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramAndCourses?.elements || [];
  const sessionDates = filterExistsOrDefault(sessions.map((session) => session?.session));

  const loading = !programId || badgeCollectionsLoading || badgeCoursesLoading || badgeCoursesSessionLoading;

  if (loading) {
    return (
      <EnterpriseBadgeCollectionsContext.Provider
        value={{ enterpriseBadgeCollectionsLoading: true, enterpriseBadgesWithCollection: [] }}
      >
        {children}
      </EnterpriseBadgeCollectionsContext.Provider>
    );
  }

  const enterpriseBadgesWithCollection =
    enterpriseBadgeCollections
      .filter((enterpriseBadge) => enterpriseBadge.productIds.length > 0)
      .map((enterpriseBadge) => ({
        __typename: enterpriseBadge.__typename,
        id: enterpriseBadge.badgeTemplateId,
        enterpriseBadge,
        collection: enterpriseBadgeCollectionToCollectionType(enterpriseBadge, courseMetadata, sessionDates),
      })) ?? [];

  return (
    <EnterpriseBadgeCollectionsContext.Provider
      value={{
        enterpriseBadgeCollectionsLoading: false,
        enterpriseBadgesWithCollection,
        refetchEnterpriseBadgeCollections,
      }}
    >
      {children}
    </EnterpriseBadgeCollectionsContext.Provider>
  );
};

const EnterpriseBadgeCollectionsProviderWithProp = ({ programFromProps, limit, children }: Props) => {
  return (
    <EnterpriseBadgeCollectionsProviderBase programId={programFromProps?.id} limit={limit}>
      {children}
    </EnterpriseBadgeCollectionsProviderBase>
  );
};

const EnterpriseBadgeCollectionsProviderWithHook = ({ limit, children }: Props) => {
  const program = useProgram();
  const programId = program?.id;
  return (
    <EnterpriseBadgeCollectionsProviderBase programId={programId} limit={limit}>
      {children}
    </EnterpriseBadgeCollectionsProviderBase>
  );
};

// This separation is required to preventing making 2 calls to fetch program.
/**
 * @description The provider is used to fetch the enterprise badge collections data.
 */
const EnterpriseBadgeCollectionsProvider = ({ programFromProps, limit, children }: Props) => {
  if (programFromProps) {
    return (
      <EnterpriseBadgeCollectionsProviderWithProp programFromProps={programFromProps} limit={limit}>
        {children}
      </EnterpriseBadgeCollectionsProviderWithProp>
    );
  } else {
    return (
      <EnterpriseBadgeCollectionsProviderWithHook limit={limit}>{children}</EnterpriseBadgeCollectionsProviderWithHook>
    );
  }
};

export default EnterpriseBadgeCollectionsProvider;
